import React, { useMemo, useState } from 'react';
import { create, InstanceProps } from 'react-modal-promise';
import { useRouter } from 'next/router';

import { Icon, IconButton, Spinner } from 'components';
import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import { Avatar } from 'components/Avatar/Avatar.component';
import Modal from 'components/Modal';
import { useSponsorshipInvite } from 'hooks/useSponsorshipInvite';
import { useNotification } from 'hooks/useNotification';
import { useSponsorCompaniesInternal } from 'hooks/useSponsorCompaniesInternal';
import { PAGE_ROUTES } from 'lib/page-routes';
import { SponsorCompanyAttributes } from 'lib/models/sponsor-companies';
import { parseResponse } from 'lib/utils/parser';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { SponsorShipPlanType } from 'lib/models/sponsorship-plans';
import { API_ROUTES } from 'lib/api-routes';
import { getCurrency, durationType } from 'utils/format';
import styles from './ReviewOfferModal.module.scss';

interface ModalInstanceProps {
  invite: SponsorCompanyAttributes;
  isSociety: boolean;
}

const ModalInstance: React.FC<ModalInstanceProps & InstanceProps<number>> = ({
  isOpen,
  onResolve,
  onReject,
  invite,
  isSociety,
}) => {
  const router = useRouter();
  const notificationInstance = useNotification();
  const { onAcceptRejectSponsorInvite, mutateSponsorInviteList } =
    useSponsorshipInvite();
  const { mutateSponsorshipData } = useSponsorCompaniesInternal();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    id: invite_id,
    student_society: studentSocietyResponse,
    sponsorship_plan: planResponse,
    room_id,
    message_id,
  } = invite;

  const society = parseResponse<StudentSocietyAttributes>(
    studentSocietyResponse
  );
  const offer = parseResponse<SponsorShipPlanType>(planResponse);

  const { name: societyName, icon_url } = society || {};

  const onRequestReject = async () => {
    setIsLoading(true);
    try {
      const api_pathname = `${
        isSociety
          ? API_ROUTES.SOCIETY_INVITE_SPONSORS
          : API_ROUTES.COMPANY_SPONSORSHIP_INVITE_LIST
      }/${invite_id}/reject`;
      const response = await onAcceptRejectSponsorInvite(api_pathname);
      if (response) {
        isSociety ? mutateSponsorshipData() : mutateSponsorInviteList();
        notificationInstance.success({
          title: 'Offer rejected!',
          message: '',
        });
        setIsLoading(false);
        onResolve(2);
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const onRequestAccept = async () => {
    setIsLoading(true);
    try {
      const api_pathname = `${
        isSociety
          ? API_ROUTES.SOCIETY_INVITE_SPONSORS
          : API_ROUTES.COMPANY_SPONSORSHIP_INVITE_LIST
      }/${invite_id}/accept`;
      const response = await onAcceptRejectSponsorInvite(api_pathname);
      if (response) {
        isSociety ? mutateSponsorshipData() : mutateSponsorInviteList();
        notificationInstance.success({
          title: 'Offer accepted!',
          message: '',
        });
        setIsLoading(false);
        onResolve(1);
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const messageSection = useMemo(() => {
    const { cover_letters } = invite || {};

    return cover_letters ? (
      <Row columnGap={4} wrap={false} className={styles['message-section']}>
        <Avatar
          name={societyName}
          size="small"
          icon={icon_url}
          className={styles.messageAvatar}
        />
        <div className={styles.messageContainer}>
          <p
            className={styles.messageContent}
            dangerouslySetInnerHTML={{
              __html: cover_letters.replace(/\n/g, '<br />'),
            }}
          />
        </div>
      </Row>
    ) : null;
  }, [invite]);

  const planSection = useMemo(() => {
    if (offer) {
      const {
        title,
        cost,
        duration_type,
        currency,
        custom_features,
        huzzle_features,
      } = offer;
      const filterCustomFeatures = custom_features.filter(
        (item) => item && item !== ' '
      );
      const features = [
        ...huzzle_features.filter((feature) => feature),
        ...filterCustomFeatures,
      ];

      return (
        <div className={styles['plan-section']}>
          <Row align="center" justify="space-between" wrap={false}>
            <p className={styles.planTitle}>{title || 'Custom Plan'}</p>
            {cost ? (
              <p className={styles.planCost}>
                {getCurrency(currency)}
                {cost} <span>{durationType(duration_type)}</span>
              </p>
            ) : null}
          </Row>
          <div className={styles.planSeparator}></div>
          <p className={styles.planSubtitle}>Features</p>
          {features?.map((feature, index) => {
            return (
              <Row
                key={index}
                wrap={false}
                columnGap={8}
                className={styles.planSubtitleRow}
              >
                <Icon
                  iconName="icon_check"
                  size="small"
                  className={styles.planSubtitleIcon}
                />
                <p className={styles.planSubtitleText}>{feature}</p>
              </Row>
            );
          })}
        </div>
      );
    }
  }, [offer]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onReject}
      className={styles.modal}
      contentClassName={styles.modalContent}
    >
      {isLoading && <Spinner showBackDrop size="small" />}
      <IconButton
        size="xsmall"
        iconName="close"
        className={styles.cancel}
        onClick={() => onReject()}
      />

      <div className={styles.header}>
        <p className={styles.title}>Review offer</p>
        {planSection}
        {messageSection}
      </div>
      <div className={styles['footer-section']}>
        <div className={styles.separator}></div>
        <Row
          align="center"
          justify="end"
          columnGap={8}
          isFullWidthRow
          className={styles.buttons}
        >
          <ButtonV3 startIcon="icon_check" onClick={onRequestAccept}>
            Accept
          </ButtonV3>
          <ButtonV3
            color="secondary"
            startIcon="icon_close"
            onClick={onRequestReject}
          >
            Reject
          </ButtonV3>
          <ButtonV3
            color="secondary"
            onClick={() => {
              if (router.pathname === PAGE_ROUTES.MESSAGES) {
                onReject();
              } else {
                router.push(
                  `${PAGE_ROUTES.MESSAGES}?roomId=${room_id}&message_id=${message_id}`
                );
                onReject();
              }
            }}
          >
            <Icon iconName="icon_chat" />
          </ButtonV3>
        </Row>
      </div>
    </Modal>
  );
};

export const reviewOfferModalInstance = create(ModalInstance);
